import { useFlowControlStore } from '@components/flow-controller'
import { type MaybeRefOrGetter, computed, toValue } from 'vue'

import { type FlowReservation, subtotalFromCartFees } from '@/helpers/flowPricing'
import { fromSelectionsSummary } from '@/helpers/selectionRows'

import { useFlags } from '@/composables/useFlags'

export function useFlowSelections(soldOut: MaybeRefOrGetter<boolean> = false) {
  const store = useFlowControlStore()
  const flags = useFlags()

  const reservations = computed<FlowReservation[]>(
    () => store.cartSnapshot?.flowResolutions.flatMap((resolution) => resolution.resolution?.reservations ?? []) ?? [],
  )

  const rows = computed(() =>
    fromSelectionsSummary({
      rows: store.selectionsSummary,
      previousInputs: store.previousInputs,
      previousPrompts: store.previousPrompts,
      tickets: store.cartSnapshot?.tickets,
      reservations: reservations.value,
    }),
  )

  /**
   * The API's own total, or `null` while it would not price exactly the rows on screen. An empty
   * cart still comes back with a zeroed stripe fee, so the fee alone would read `$0.00`.
   *
   * TODO TIC-6248: Withholding may not be the right long-term UX. Confirm once API changes land.
   */
  const subtotal = computed(() => {
    const nothingReserved = !store.cartSnapshot?.tickets.length
    if (store.pricingStale || nothingReserved) return null

    return subtotalFromCartFees(store.cartSnapshot?.cartFees)
  })

  const selectionsLayout = computed(() => Boolean(flags.value.flows_progress_sidebar) && !toValue(soldOut))
  const showSummary = computed(() => selectionsLayout.value && store.action?.type !== 'confirm')
  const showTrigger = computed(() => showSummary.value && rows.value.length > 0)

  return { rows, subtotal, selectionsLayout, showSummary, showTrigger, editStep: store.goBackTo }
}
